import { useMemo, useState, useEffect } from 'react'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CustomTable } from '../../../../components/customTable'
import { PagePreloader } from '../../../../components/pagePreloader'
import { SmoothTransition } from '../../../../components/pageTransition'
import {
    RenderDefaultActions,
    RenderStatus,
} from '../../common'
import { usePopup } from '../../../../context/popupContext'
import { useNotificationBar } from '../../../../context/notificationContext'

import {
    getErrorResponse,
    getUpdatedSearchParams,
    parseSearchParams,
} from '../../../../services/util'
import {
    useGetTools,
    useDeleteTool,
    useStatusTool,
} from 'hooks/tools'

const ToolsList = () => {
    const navigate = useNavigate()

    // popup/notification
    const popup = usePopup()
    const notification = useNotificationBar()

    // selection
    const rowSelectionState = useState<RowSelectionState>({})

    // API hooks
    const { mutate: deleteTool } = useDeleteTool()
    const { mutate: toolStatus } = useStatusTool()

    // Filter Params
    const [searchParams, setSearchParams] = useSearchParams()
    const filterParams: any = parseSearchParams(searchParams, 'filter')

    // fetch data
    const {
        data,
        isLoading,
        refetch,
        isError,
        dataUpdatedAt,
    } = useGetTools(filterParams, {
        keepPreviousData: true,
    })

    const useFormHook = useForm({
        defaultValues: filterParams,
    })

    const responseData = useMemo(() => {
        if (!data?.data) return null

        return {
            ...data.data,
            results: Array.isArray(data.data.results)
                ? data.data.results
                : [],
        }
    }, [data])

    const openAddUpdatePage = (row: any, type: 'edit' | 'add') => {
        if (type === 'edit' && row?.toolId) {
            navigate(`/master/tools-master/edit/${row.toolId}`)
            return
        }
        if (type === 'add') {
            navigate('/master/tools-master/create')
        }
    }

    // -------------------------------
    //  STATUS TOGGLE
    // -------------------------------
    const onStatusChange = (row: any) => {
        const nextStatus = row.status ? 0 : 1

        popup.open({
            title: 'Are you sure?',
            message: `Do you want to ${nextStatus ? 'Activate' : 'Deactivate'} ${row.toolName || 'this'} Tool?`,
            type: 'confirm',

            onAction: (value: boolean) => {
                if (!value) return

                const payload = { status: Number(nextStatus) }

                toolStatus(
                    {
                        id: row.toolId || row.id,
                        data: payload,
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: 'Success!',
                                message: `Tool status updated successfully!`,
                                type: 'success',
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        },
                    }
                )
            },
        })
    }

    // -------------------------------
    //  DELETE HANDLER
    // -------------------------------
    const onDeleteAction = (row: any) => {
        popup.open({
            title: 'Are you sure?',
            message: (
                <>
                    Do you want to delete <strong>{row?.toolName}</strong> tool? This cannot be undone.
                </>
            ),
            type: 'confirm',
            onAction: (value: boolean) => {
                if (!value) return

                deleteTool(
                    {
                        id: row?.toolId || row?.id,
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: 'Success!',
                                message: 'Tool deleted successfully!',
                                type: 'success',
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        },
                    }
                )
            },
        })
    }

    // -------------------------------
    //  GENERAL ACTION HANDLER
    // -------------------------------
    const onAction = (actionType: string, row: any) => {
        if (actionType === 'edit') openAddUpdatePage(row, 'edit')
        if (actionType === 'delete') onDeleteAction(row)
    }

    // -------------------------------
    //  TABLE COLUMNS
    // -------------------------------
    const columns: ColumnDef<any>[] = [
        {
            id: 'sr_no',
            header: 'ID',
            cell: ({ row, table }) => {
                const pageIndex = table.getState().pagination.pageIndex
                const pageSize = table.getState().pagination.pageSize

                return pageIndex * pageSize + row.index + 1
            },
            enableSorting: false,
        },
        {
            accessorKey: 'toolName',
            header: 'TOOL NAME',
        },
        {
            accessorKey: 'tool_type',
            header: 'TOOL TYPE',
            cell: ({ getValue }) => {
                const value = getValue() as string
                return value || '-'
            },
        },
        {
            accessorKey: 'display',
            header: 'DISPLAY',
            cell: ({ getValue }) => {
                const value = getValue() as string
                return value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
            },
        },
        {
            accessorKey: 'item_type',
            header: 'ITEM TYPE',
            cell: ({ getValue }) => {
                const value = getValue() as string
                return value || '-'
            },
        },
        {
            accessorKey: 'item_name',
            header: 'ITEM NAME',
            cell: ({ getValue }) => getValue() || '-',
        },
        {
            accessorKey: 'createdAt',
            header: 'CREATED DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null
                return value ? moment(value).format('LL') : '-'
            },
        },
        {
            id: 'status',
            header: 'STATUS',
            accessorFn: (row) => Number(row.status),
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
        },
        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) =>
                RenderDefaultActions(row, ['edit', 'delete'], onAction),
        },
    ]

    // Handle form filter changes
    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (!name) return

            const updated = getUpdatedSearchParams(filterParams, values, name)

            if (Object.keys(updated)?.length) {
                const sp = encodeURIComponent(JSON.stringify(updated))
                setSearchParams({ filter: sp })
            } else {
                setSearchParams({})
            }
        })

        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

        // -------------------------------
        //  RENDER
        // -------------------------------
        return (
            <div className="w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
                
                <SmoothTransition>
                    {isLoading ? (
                        <PagePreloader />
                    ) : isError ? (
                        <div className="text-center py-12">
                            <p className="text-red-500">Failed to load tools. Please try again.</p>
                        </div>
                    ) : (
                        <CustomTable
                            title="Tools"
                            titleLabel={`Last updated ${moment(
                                dataUpdatedAt
                            ).fromNow()}`}
                            columns={columns}
                            data={responseData?.results}
                            pages={responseData?.pages}
                            totalRecords={responseData?.total}
                            isLoading={isLoading}
                            isErrored={isError}
                            formHook={useFormHook}
                            rowSelectionState={rowSelectionState}
                            onRefresh={refetch}
                            onAddAction={() => openAddUpdatePage(null, 'add')}
                        />
                    )}
                </SmoothTransition>
            </div>
        )
    }

export default ToolsList
